
import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { FileText, Building2, MapPin, Hash, Loader2 } from 'lucide-react';
import { TaxInvoiceFormData, SELLER_INFO } from '../../types/taxInvoice';

interface TaxInvoiceFormProps {
  companyName: string;
  brandName: string;
  contactPerson: string;
  email: string;
  mobile: string;
  subtotal: number;
  vatAmount: number;
  totalAmount: number;
  paymentMethod: 'creditcard' | 'bank_transfer';
  onSubmit: (data: TaxInvoiceFormData) => void;
  onCancel: () => void;
  isGenerating: boolean;
}

export const TaxInvoiceForm = ({
  companyName,
  brandName,
  contactPerson,
  email,
  mobile,
  subtotal,
  vatAmount,
  totalAmount,
  paymentMethod,
  onSubmit,
  onCancel,
  isGenerating
}: TaxInvoiceFormProps) => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';

  const [formData, setFormData] = useState<TaxInvoiceFormData>({
    buyerAddress: '',
    buyerCity: '',
    buyerPostalCode: '',
    buyerVatNumber: '',
    buyerCrNumber: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateVatNumber = (vat: string): boolean => {
    // Saudi VAT numbers are 15 digits starting with 3
    const vatRegex = /^3\d{14}$/;
    return vatRegex.test(vat);
  };

  const handleInputChange = (field: keyof TaxInvoiceFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.buyerAddress.trim()) {
      newErrors.buyerAddress = isRtl ? 'العنوان مطلوب' : 'Address is required';
    }
    if (!formData.buyerCity.trim()) {
      newErrors.buyerCity = isRtl ? 'المدينة مطلوبة' : 'City is required';
    }
    if (!formData.buyerPostalCode.trim()) {
      newErrors.buyerPostalCode = isRtl ? 'الرمز البريدي مطلوب' : 'Postal code is required';
    }
    if (!formData.buyerVatNumber.trim()) {
      newErrors.buyerVatNumber = isRtl ? 'رقم التسجيل الضريبي مطلوب' : 'VAT number is required';
    } else if (!validateVatNumber(formData.buyerVatNumber)) {
      newErrors.buyerVatNumber = isRtl 
        ? 'رقم ضريبي غير صالح (يجب أن يكون 15 رقم يبدأ بـ 3)' 
        : 'Invalid VAT number (must be 15 digits starting with 3)';
    }

    // CR Number - now mandatory
    if (!formData.buyerCrNumber?.trim()) {
      newErrors.buyerCrNumber = isRtl ? 'السجل التجاري مطلوب' : 'Commercial Registration is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  const formatCurrency = (amount: number) => {
    const locale = isRtl ? 'ar-SA' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div className={`space-y-6 ${isRtl ? 'rtl' : 'ltr'}`} dir={isRtl ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg">
          <FileText className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground">
            {isRtl ? 'فاتورة ضريبية' : 'Tax Invoice'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isRtl ? 'أدخل بيانات الفوترة لإنشاء الفاتورة' : 'Enter billing details to generate invoice'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Seller Information (Read-only) */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              {isRtl ? 'البائع' : 'Seller'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{isRtl ? 'الاسم:' : 'Name:'}</span>
              <span className="font-medium">{isRtl ? SELLER_INFO.nameAr : SELLER_INFO.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{isRtl ? 'العنوان:' : 'Address:'}</span>
              <span className="font-medium">{SELLER_INFO.addressAr}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{isRtl ? 'المدينة:' : 'City:'}</span>
              <span className="font-medium">{SELLER_INFO.city}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{isRtl ? 'الرمز البريدي:' : 'Postal Code:'}</span>
              <span className="font-medium">{SELLER_INFO.postalCode}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{isRtl ? 'الرقم الضريبي:' : 'VAT Number:'}</span>
              <span className="font-medium font-mono">{SELLER_INFO.vatNumber}</span>
            </div>
          </CardContent>
        </Card>

        {/* Pre-filled Buyer Information */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              {isRtl ? 'المشتري' : 'Buyer'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{isRtl ? 'اسم الشركة:' : 'Company:'}</span>
              <span className="font-medium">{companyName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{isRtl ? 'العلامة التجارية:' : 'Brand:'}</span>
              <span className="font-medium">{brandName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{isRtl ? 'المسؤول:' : 'Contact:'}</span>
              <span className="font-medium">{contactPerson}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{isRtl ? 'البريد:' : 'Email:'}</span>
              <span className="font-medium">{email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{isRtl ? 'الجوال:' : 'Mobile:'}</span>
              <span className="font-medium">{mobile}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Required Buyer Tax Information */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            {isRtl ? 'بيانات الفوترة المطلوبة' : 'Required Billing Information'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="buyerAddress">
                {isRtl ? 'العنوان' : 'Address'} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="buyerAddress"
                value={formData.buyerAddress}
                onChange={(e) => handleInputChange('buyerAddress', e.target.value)}
                placeholder={isRtl ? 'أدخل العنوان الكامل' : 'Enter full address'}
                className={errors.buyerAddress ? 'border-destructive' : ''}
              />
              {errors.buyerAddress && (
                <p className="text-xs text-destructive">{errors.buyerAddress}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="buyerCity">
                {isRtl ? 'المدينة' : 'City'} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="buyerCity"
                value={formData.buyerCity}
                onChange={(e) => handleInputChange('buyerCity', e.target.value)}
                placeholder={isRtl ? 'أدخل المدينة' : 'Enter city'}
                className={errors.buyerCity ? 'border-destructive' : ''}
              />
              {errors.buyerCity && (
                <p className="text-xs text-destructive">{errors.buyerCity}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="buyerPostalCode">
                {isRtl ? 'الرمز البريدي' : 'Postal Code'} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="buyerPostalCode"
                value={formData.buyerPostalCode}
                onChange={(e) => handleInputChange('buyerPostalCode', e.target.value)}
                placeholder={isRtl ? 'أدخل الرمز البريدي' : 'Enter postal code'}
                className={errors.buyerPostalCode ? 'border-destructive' : ''}
              />
              {errors.buyerPostalCode && (
                <p className="text-xs text-destructive">{errors.buyerPostalCode}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="buyerVatNumber">
                {isRtl ? 'رقم التسجيل الضريبي' : 'VAT Registration Number'} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="buyerVatNumber"
                value={formData.buyerVatNumber}
                onChange={(e) => handleInputChange('buyerVatNumber', e.target.value)}
                placeholder={isRtl ? '3XXXXXXXXXXXXXX' : '3XXXXXXXXXXXXXX'}
                className={`font-mono ${errors.buyerVatNumber ? 'border-destructive' : ''}`}
                maxLength={15}
              />
              {errors.buyerVatNumber && (
                <p className="text-xs text-destructive">{errors.buyerVatNumber}</p>
              )}
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="buyerCrNumber" className="flex items-center gap-2">
                <Hash className="h-4 w-4" />
                {isRtl ? 'السجل التجاري' : 'Commercial Registration'} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="buyerCrNumber"
                value={formData.buyerCrNumber || ''}
                onChange={(e) => handleInputChange('buyerCrNumber', e.target.value)}
                placeholder={isRtl ? 'أدخل رقم السجل التجاري' : 'Enter CR number'}
                className={errors.buyerCrNumber ? 'border-destructive' : ''}
              />
              {errors.buyerCrNumber && (
                <p className="text-xs text-destructive">{errors.buyerCrNumber}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Financial Summary */}
      <Card className="bg-muted/30">
        <CardContent className="pt-6">
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{isRtl ? 'المجموع الفرعي:' : 'Subtotal:'}</span>
              <span className="font-medium">{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{isRtl ? 'ضريبة القيمة المضافة (15%):' : 'VAT (15%):'}</span>
              <span className="font-medium">{formatCurrency(vatAmount)}</span>
            </div>
            <Separator />
            <div className="flex justify-between text-lg font-bold">
              <span>{isRtl ? 'الإجمالي:' : 'Total:'}</span>
              <span className="text-primary">{formatCurrency(totalAmount)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-3 justify-end">
        <Button variant="outline" onClick={onCancel} disabled={isGenerating}>
          {isRtl ? 'إلغاء' : 'Cancel'}
        </Button>
        <Button onClick={handleSubmit} disabled={isGenerating}>
          {isGenerating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              {isRtl ? 'جاري الإنشاء...' : 'Generating...'}
            </>
          ) : (
            <>
              <FileText className="h-4 w-4 mr-2" />
              {isRtl ? 'إنشاء الفاتورة' : 'Generate Invoice'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
